import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ProductDetailsModal } from "../../interfaces/ProductObject";
import { ConvertedDineInSession } from "../../services/converters/backendToUi/dineInConvertor";
import {
  fetchDineInBoardSessions,
  refreshDineInBoardSessions,
  fetchDineInBoardProducts,
  decideDineInItems,
  decideDineInRound,
} from "../../services/thunks/DineInBoardThunk";
import { resetAllState } from "./UiDb";

// ─── Dine In — business/kitchen board ─────────────────────────────────────
// Every live table at one business, for the staff-facing Dine-in tab.
//
// Deliberately NOT part of DineInDb. That slice is a single-session state
// machine for a DINER: it carries `phase`, `codeError` and `hostName`, and its
// applySessionResponse writes a table code to local storage and sets the
// global "you are currently seated at this table" marker that makes the
// Products tab offer "add to dine-in session". Pushing a floor's worth of
// sessions through it would fight that state machine on every fetch and could
// leave a staff member's phone believing it is seated at whichever table
// happened to be converted last. Separate concerns, separate slices; the only
// thing they share is the converter and the product-fetch helper.

interface DineInBoardState {
  /**
   * Which business the sessions below belong to. Guards against a stale render
   * when the user moves between two business pages faster than the fetches
   * resolve — a late response for the previous business is dropped rather than
   * painted over the new one.
   */
  businessId: string | null;
  sessions: ConvertedDineInSession[];
  /** Resolved product details for every productId referenced by any session. */
  products: ProductDetailsModal[];
  /** Epoch ms of the last successful load, for a "as of HH:MM" line. */
  lastFetchedAt: number | null;
  loadingMap: Record<string, string | null>;
  errorMap: Record<string, string | null>;
  successMap: Record<string, boolean>;
}

const initialState: DineInBoardState = {
  businessId: null,
  sessions: [],
  products: [],
  lastFetchedAt: null,
  loadingMap: {},
  errorMap: {},
  successMap: {},
};

/** Shared by the loud and silent fetches — see the businessId guard above. */
function applySessions(
  state: DineInBoardState,
  payload: { businessId: string; sessions: ConvertedDineInSession[] }
): void {
  if (state.businessId && state.businessId !== payload.businessId) return;
  state.businessId = payload.businessId;
  state.sessions = payload.sessions;
  state.lastFetchedAt = Date.now();
}

/**
 * Applies a single session returned by a mutation back into the floor.
 *
 * Every dine-in write echoes the whole session, so this replaces that one
 * table outright rather than patching fields — no partial merge to get wrong.
 * A session that isn't in the list is appended, which covers the case where a
 * table was seated between the last fetch and this write.
 */
function applyOneSession(state: DineInBoardState, updated: ConvertedDineInSession): void {
  if (state.businessId && updated.session.businessId !== state.businessId) return;
  const index = state.sessions.findIndex(
    (entry) => entry.session.sessionId === updated.session.sessionId
  );
  if (index >= 0) state.sessions[index] = updated;
  else state.sessions.push(updated);
  state.lastFetchedAt = Date.now();
}

const dineInBoardSlice = createSlice({
  name: "dineInBoard",
  initialState,
  reducers: {
    /**
     * Called when the tab mounts for a business, BEFORE the first fetch —
     * clears another business's tables so they can't flash on screen under the
     * new business's name while the request is in flight.
     */
    openBoardForBusiness(state, action: PayloadAction<string>) {
      if (state.businessId === action.payload) return;
      state.businessId = action.payload;
      state.sessions = [];
      state.products = [];
      state.lastFetchedAt = null;
      state.errorMap = {};
      state.loadingMap = {};
      state.successMap = {};
    },
    /** Leaving the tab — drops the floor state so it can't be served stale later. */
    closeBoard() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDineInBoardSessions.pending, (state) => {
        state.loadingMap.fetchSessions = "Loading tables...";
        state.errorMap.fetchSessions = null;
      })
      .addCase(fetchDineInBoardSessions.fulfilled, (state, action) => {
        state.loadingMap.fetchSessions = null;
        state.successMap.fetchSessions = true;
        applySessions(state, action.payload);
      })
      .addCase(fetchDineInBoardSessions.rejected, (state, action) => {
        state.loadingMap.fetchSessions = null;
        state.errorMap.fetchSessions = action.payload as string;
      })

      // Silent refresh — no loadingMap, no errorMap, by design. A background
      // refetch must never blank a live floor into skeletons or replace it
      // with a retry screen; see refreshDineInBoardSessions' doc comment.
      .addCase(refreshDineInBoardSessions.fulfilled, (state, action) => {
        applySessions(state, action.payload);
      })
      .addCase(refreshDineInBoardSessions.rejected, () => {})

      .addCase(fetchDineInBoardProducts.pending, (state) => {
        state.loadingMap.fetchProducts = "Loading menu items...";
        state.errorMap.fetchProducts = null;
      })
      .addCase(fetchDineInBoardProducts.fulfilled, (state, action) => {
        state.loadingMap.fetchProducts = null;
        // Merge rather than replace — products resolve incrementally as new
        // tables order things we haven't looked up yet, and a replace would
        // drop the price of everything already on the floor.
        const existingIds = new Set(state.products.map((p) => p.productId));
        for (const product of action.payload) {
          if (!existingIds.has(product.productId)) state.products.push(product);
        }
      })
      .addCase(fetchDineInBoardProducts.rejected, (state, action) => {
        state.loadingMap.fetchProducts = null;
        state.errorMap.fetchProducts = action.payload as string;
      })

      // Kitchen accept/reject. Unlike the silent refresh above this DOES set
      // loadingMap — the buttons disable off it, and a tap that looks inert
      // invites a second tap, which on this screen means deciding the same
      // dish twice.
      .addCase(decideDineInItems.pending, (state) => {
        state.loadingMap.decideItems = "Updating...";
        state.errorMap.decideItems = null;
      })
      .addCase(decideDineInItems.fulfilled, (state, action) => {
        state.loadingMap.decideItems = null;
        applyOneSession(state, action.payload);
      })
      .addCase(decideDineInItems.rejected, (state, action) => {
        state.loadingMap.decideItems = null;
        state.errorMap.decideItems = action.payload as string;
      })

      // Round-level decisions share decideItems' loading key on purpose: the
      // buttons that disable off it are the same buttons, and a round decision
      // in flight should lock the individual item actions inside that round
      // just as firmly.
      .addCase(decideDineInRound.pending, (state) => {
        state.loadingMap.decideItems = "Updating...";
        state.errorMap.decideItems = null;
      })
      .addCase(decideDineInRound.fulfilled, (state, action) => {
        state.loadingMap.decideItems = null;
        applyOneSession(state, action.payload);
      })
      .addCase(decideDineInRound.rejected, (state, action) => {
        state.loadingMap.decideItems = null;
        state.errorMap.decideItems = action.payload as string;
      })

      .addCase(resetAllState, () => initialState);
  },
});

export const { openBoardForBusiness, closeBoard } = dineInBoardSlice.actions;

export const selectDineInBoardBusinessId = (state: any): string | null =>
  state.dineInBoard.businessId;

export const selectDineInBoardSessions = (state: any): ConvertedDineInSession[] =>
  state.dineInBoard.sessions;

export const selectDineInBoardProducts = (state: any): ProductDetailsModal[] =>
  state.dineInBoard.products;

export const selectDineInBoardLastFetchedAt = (state: any): number | null =>
  state.dineInBoard.lastFetchedAt;

/** One table's session by table number, for the detail view. */
export const selectDineInBoardSessionByTable =
  (tableNumber: number) =>
  (state: any): ConvertedDineInSession | null =>
    (state.dineInBoard.sessions as ConvertedDineInSession[]).find(
      (s) => s.session.tableNumber === tableNumber
    ) ?? null;

export const selectDineInBoardLoadingMap = (state: any) => state.dineInBoard.loadingMap;
export const selectDineInBoardErrorMap = (state: any) => state.dineInBoard.errorMap;

export default dineInBoardSlice.reducer;
